import { useQuery } from "@apollo/client";
import prettyBytes from "pretty-bytes";

import FiltersSortHeaderStaticColumn from "components/Filters/SortHeader/StaticColumn";
import FiltersSortHeaderWrapper from "components/Filters/SortHeader/Wrapper";
import FlashContext from "components/FlashMessages/FlashContext";
import { Download, File } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Counter from "ds/components/Counter";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { TerraformProviderVersionArtifact } from "types/generated";
import { downloadFileFromURL } from "utils/file";

import { GET_DOWNLOADABLE_PROVIDER_VERSION_ARTIFACT_URL } from "../gql";
import styles from "./styles.module.css";
import {
  GetDownloadableProviderVersionArtifactUrlGql,
  ProviderVersionArtifactsProps,
} from "./type";

function ProviderVersionArtifacts({ version, provider }: ProviderVersionArtifactsProps) {
  const { onError, reportError } = useTypedContext(FlashContext);

  const { refetch: getLink, loading } = useQuery<GetDownloadableProviderVersionArtifactUrlGql>(
    GET_DOWNLOADABLE_PROVIDER_VERSION_ARTIFACT_URL,
    {
      skip: true,
      fetchPolicy: "no-cache",
    }
    // APOLLO CLIENT UPDATE
  );

  const handleDownloadArtifact = (artifact: TerraformProviderVersionArtifact) => {
    getLink({
      artifactFilename: artifact.filename,
      versionNumber: version.number,
      providerId: provider.id,
    })
      .then(({ data }) => {
        if (data?.terraformProvider?.version?.artifactDownloadUrl) {
          downloadFileFromURL(data.terraformProvider.version.artifactDownloadUrl);
        } else {
          reportError({
            message: "Something went wrong while generating a download URL, please try again.",
          });
        }
      })
      .catch(onError);
  };

  return (
    <Box className={styles.tableWrapper} direction="column" fullWidth>
      <Box className={styles.tableHeader} align="center" gap="medium">
        <Typography tag="span" variant="p-t6">
          Assets
        </Typography>
        <Counter count={version.artifacts.length} />
      </Box>
      <FiltersSortHeaderWrapper
        columnOrder="minmax(40rem, 7fr) minmax(5rem, 1fr) 2.4rem"
        columnGap="large"
        ariaRole="row"
      >
        <FiltersSortHeaderStaticColumn role="columnheader">
          <Typography tag="span" variant="p-t7">
            Name
          </Typography>
        </FiltersSortHeaderStaticColumn>
        <FiltersSortHeaderStaticColumn role="columnheader">
          <Typography tag="span" variant="p-t7">
            Size
          </Typography>
        </FiltersSortHeaderStaticColumn>
      </FiltersSortHeaderWrapper>
      <Box direction="column">
        {version.artifacts.map((artifact) => (
          <Box
            className={styles.tableRow}
            key={artifact.filename}
            grid
            gap="large"
            gridTemplate="minmax(40rem, 7fr) minmax(5rem, 1fr) 2rem"
            role="row"
          >
            <Box align="center" gap="medium" role="rowheader">
              <Icon src={File} />
              <Typography tag="span" variant="p-body2">
                {artifact.filename}
              </Typography>
            </Box>
            <Box align="center" role="cell">
              <Typography tag="span" variant="p-body2">
                {prettyBytes(artifact.sizeBytes)}
              </Typography>
            </Box>
            <Box align="center" role="cell">
              <ButtonIcon
                onPress={() => handleDownloadArtifact(artifact)}
                variant="secondary"
                size="small"
                disabled={loading}
                icon={Download}
                tooltipPlacement="left"
              >
                Download artifact
              </ButtonIcon>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default ProviderVersionArtifacts;
